import React from "react";
import { toast as sonnerToast } from "sonner";
import { Check, X, AlertTriangle, Loader2 } from "lucide-react";

type ToastOptions = Parameters<typeof sonnerToast>[1];
type ExternalToastOptions = NonNullable<ToastOptions>;
type PromiseOptions = Parameters<typeof sonnerToast.promise>[1];

const SuccessIcon = <Check className="h-4 w-4 text-gray-500 dark:text-gray-400" aria-hidden />;
const ErrorIcon = <X className="h-4 w-4 text-gray-500 dark:text-gray-400" aria-hidden />;
const DefaultIcon = <AlertTriangle className="h-4 w-4 text-gray-500 dark:text-gray-400" aria-hidden />;

function withToastIcon(options: ToastOptions, icon: React.ReactNode): ToastOptions {
  if (!options) {
    return { icon } as ExternalToastOptions;
  }
  if (typeof options !== "object") {
    return options;
  }
  if ((options as ExternalToastOptions).icon !== undefined) {
    return options;
  }
  return { ...(options as ExternalToastOptions), icon };
}

function withPromiseIcon<T>(value: T, icon: React.ReactNode): T {
  if (value == null) {
    return ({ icon } as unknown) as T;
  }

  if (typeof value === "string") {
    return ({ description: value, icon } as unknown) as T;
  }

  if (typeof value === "function" || React.isValidElement(value)) {
    return value;
  }

  if (typeof value === "object") {
    return ({ ...(value as Record<string, unknown>), icon } as unknown) as T;
  }

  return value;
}

const toast = ((
  message: Parameters<typeof sonnerToast>[0],
  options?: ToastOptions
) => sonnerToast(message, withToastIcon(options, DefaultIcon))) as typeof sonnerToast;

Object.assign(toast, sonnerToast);

toast.success = (
  message: Parameters<typeof sonnerToast.success>[0],
  options?: Parameters<typeof sonnerToast.success>[1]
) => sonnerToast.success(message, withToastIcon(options, SuccessIcon));

toast.error = (
  message: Parameters<typeof sonnerToast.error>[0],
  options?: Parameters<typeof sonnerToast.error>[1]
) => sonnerToast.error(message, withToastIcon(options, ErrorIcon));

toast.promise = ((
  promise: Parameters<typeof sonnerToast.promise>[0],
  options: PromiseOptions
) =>
  sonnerToast.promise(promise, {
    ...options,
    success: withPromiseIcon(options?.success, SuccessIcon),
    error: withPromiseIcon(options?.error, ErrorIcon),
    loading: withPromiseIcon(
      options?.loading,
      <Loader2 className="h-4 w-4 animate-spin text-gray-500 dark:text-gray-400" aria-hidden />
    ),
  })) as typeof sonnerToast.promise;

export { toast };
